import {
    Component,
    computed,
    inject,
    input,
    model,
    output,
    signal,
    WritableSignal,
    ChangeDetectionStrategy,
} from '@angular/core';
import {
    FieldAndInput,
    FormBlueprint,
    FormField,
    Group,
    ListInput,
} from './formTypes';
import {
    AbstractControl,
    FormBuilder,
    FormControlName,
    FormGroup,
    FormsModule,
    ReactiveFormsModule,
    ValidationErrors,
    ValidatorFn,
    Validators,
} from '@angular/forms';
import { tap } from 'rxjs';
import { CommonModule } from '@angular/common';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatCardModule } from '@angular/material/card';
import { MatDivider } from '@angular/material/divider';
import { MatSelectModule } from '@angular/material/select';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatDatepickerModule } from '@angular/material/datepicker';

@Component({
    selector: 'app-form',
    imports: [
        CommonModule,
        MatFormFieldModule,
        MatCardModule,
        MatDivider,
        MatSelectModule,
        MatAutocompleteModule,
        MatListModule,
        MatMenuModule,
        ReactiveFormsModule,
        MatInputModule,
        MatButtonModule,
        MatCheckboxModule,
        MatSlideToggleModule,
        MatDatepickerModule,
    ],
    templateUrl: './form.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: './form.component.css',
})
export class FormComponent {
    public fb: FormBuilder = inject(FormBuilder);

    public formBlueprint = input.required<FormBlueprint>();
    public context = input<any>();
    public isDialog = input<boolean>(false);
    public values = model<{ [key: string]: any }>({});
    public title = input.required<string>();
    formGroup: WritableSignal<FormGroup | undefined> = signal(undefined);
    confirmDisabled = input<boolean>(false);
    confirm = output<{ [key: string]: any }>();
    cancel = output<string>();
    readonly initialValue = input<{ [key: string]: any }>({});

    public setFormFields = (data: { [key: string]: any }) => {
        Object.keys(data).forEach((x) =>
            this.formGroup()?.get(x)?.setValue(data[x]),
        );
    };

    formContext = computed(() => ({
        ...this.context(),
        ...this.values(),
    }));

    ngAfterViewInit() {
        if (!Object.keys(this.values()).length)
            this.values.set(this.formBlueprint().values);
        this.formGroup.set(this.fb.group(this.setupFormGroup()));
        this.setupControllers();
        this.setFormFields(this.initialValue());
        this.formBlueprint().clearValue = this.clearField;
        this.formBlueprint().setValue = this.setField;
        this.formBlueprint().context = this.context();
    }
    public onConfirm = () => {
        const values = this.values();

        //Filter and remove undefined fields
        Object.entries(values)
            .filter(([key, value]) => {
                const field = this.formBlueprint().fields.get(key)?.field;
                return (
                    !field ||
                    field.notReturned ||
                    value === undefined ||
                    value === ''
                );
            })
            .forEach(([key, value]) => {
                delete values[key];
            });

        this.confirm.emit(values);
    };
    private clearField = (key: string) => {
        if (!Object.keys(this.values()).includes(key))
            throw new Error('Fields do not exist aka bad key');

        this.formGroup()?.get(key)?.reset();
        this.values()[key] = undefined;
    };
    private setField = (key: string, value: any) => {
        if (!Object.keys(this.values()).includes(key))
            throw new Error('Fields do not exist aka bad key');

        this.formGroup()?.get(key)?.setValue(value);
        this.values()[key] = value;
    };
    private emitChangeEvent(emitterKey: string) {
        this.formBlueprint().fields.forEach((formField) => {
            if (formField.input?.fieldListener) {
                formField.input?.fieldListener(
                    emitterKey,
                    formField.field.key,
                    this.formBlueprint(),
                    this.values(),
                );
            }
        });
    }
    private setupControllers() {
        const formGroup = this.formGroup();
        if (!formGroup) return;
        this.formBlueprint().fields.forEach((fieldInput) => {
            if (!fieldInput.field || !fieldInput.input) return;
            formGroup.controls[fieldInput.field.key].valueChanges
                .pipe(
                    tap((value) => {
                        if (fieldInput.field)
                            this.values.update((values) => ({
                                ...values,
                                ...fieldInput.field.value(value),
                            }));
                    }),
                    tap((value) => {
                        if (fieldInput.input.updateChange)
                            fieldInput.input.updateChange(value);
                    }),
                    tap(() => this.emitChangeEvent(fieldInput.field.key)),
                )
                .subscribe();
        });
    }

    private setupFormGroup(): any {
        const fromGroup: any = {};
        this.formBlueprint().fields.forEach((fieldInput) => {
            if (fieldInput.field && fieldInput.input)
                fromGroup[fieldInput.field.key] = [
                    this.values()[fieldInput.field.key],
                    {
                        validators: this.getValidator(fieldInput.input),
                    },
                ];
        });
        return fromGroup;
    }
    getValidator = (input: any): any[] => {
        const validator = input.customValidator
            ? input.customValidator(input, this.values)
            : undefined;
        return validator
            ? input.required
                ? [Validators.required, validator]
                : [validator]
            : input.required
              ? [Validators.required]
              : [];
    };
    getGroup = (field: FormField): Group | undefined =>
        (field as Group).caption ? (field as Group) : undefined;
    getFieldInput = (field: FormField): FieldAndInput | undefined =>
        (field as FieldAndInput).field.key
            ? (field as FieldAndInput)
            : undefined;
}
